import type { BlockDiagramViewSettings, GetLeftMenuArgs } from './types';
import {
  showOnlyConnectedComponents,
  dropdownTraversalMenu,
} from '@ardoq/settings-bar';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  type SettingsConfig,
  SettingsType,
  type ToggleConfig,
  DropdownConfig,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { GraphComponent, LayoutOrientation } from '@ardoq/yfiles';
import { ViewIds } from '@ardoq/api-types';
import { isInScopeDiffMode, isLoadingScope } from 'scope/scopeDiff';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { EXPORT_FORMAT, getExportsForYFilesView } from '@ardoq/export';
import { viewHasLegend } from 'views/metaInfoTabs';
import { toggleVisualDiff } from 'appContainer/MainAppModule/MainAppModuleSidebar/NavigatorTopBar/utils';
import {
  collapseAll,
  expandAll,
  SEPARATE_REFERENCES_TOGGLE,
} from 'tabview/relationshipDiagrams/leftMenuItems';
import type { OnViewSettingsUpdate } from '@ardoq/data-model';
import { getScenarioOutOfSyncPopoverText } from 'app/popovers/ScenarioOutOfSyncPopover';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import { Features, hasFeature } from '@ardoq/features';
import { type DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { getExportLabels } from 'views/getExportLabels';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import { isTruthy } from '@ardoq/common-helpers';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { BLOCK_DIAGRAM_LAYOUT_OPTIONS } from './consts';
import { exportToLucidchart } from 'integrations/lucidchart/utils';

const getSetLayoutOrientationOnClick =
  (direction: LayoutOrientation, viewId: ViewIds) => () => {
    handleStoreSetting(viewId, {
      layoutOrientation: direction,
    });
  };

export const getLayoutDirectionOption =
  (
    viewSettings: BlockDiagramViewSettings,
    viewId: ViewIds,
    i18nLocale: I18nLocale
  ) =>
  ([label, direction, i18nKey]: [string, LayoutOrientation, string]) => ({
    name: `layout_direction_${label.toLowerCase().replace(/\s+/g, '_')}`,
    label: t(i18nLocale, i18nKey, label),
    onClick: getSetLayoutOrientationOnClick(direction, viewId),
    type: DropdownOptionType.OPTION,
    isActive: viewSettings.layoutOrientation === direction,
  });

const separateReferencesToggle = (
  viewSettings: BlockDiagramViewSettings,
  onViewSettingsUpdate: OnViewSettingsUpdate
): ToggleConfig => ({
  ...SEPARATE_REFERENCES_TOGGLE,
  isActive: Boolean(viewSettings.separateReferences),
  onViewSettingsUpdate,
});

export const separateReferencesDropdown = (
  viewId: ViewIds,
  currentSetting: boolean,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): DropdownConfig => ({
  ...SEPARATE_REFERENCES_TOGGLE,
  label: t(
    i18nLocale,
    'appLayout:viewPaneHeader.referenceLines',
    'Reference lines'
  ),
  type: SettingsType.DROPDOWN,
  options: [
    {
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.combineReferenceLines',
        'Combine reference lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: !currentSetting,
      onClick: () =>
        onViewSettingsUpdate(viewId, { separateReferences: false }, true),
    } satisfies DropdownOption,
    {
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.separateReferenceLines',
        'Separate reference lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: currentSetting,
      onClick: () =>
        onViewSettingsUpdate(viewId, { separateReferences: true }, true),
    } satisfies DropdownOption,
  ],
});

const newGrouping = (
  viewSettings: BlockDiagramViewSettings,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): ToggleConfig => ({
  id: 'useNewGrouping',
  label: t(
    i18nLocale,
    'appLayout:viewPaneHeader.useNewGrouping',
    'Use new grouping'
  ),
  iconName: IconName.RHOMBUS,
  type: SettingsType.TOGGLE,
  isActive: Boolean(viewSettings.useNewGrouping),
  onViewSettingsUpdate,
});

export const getLeftMenu = ({
  viewSettings,
  traversedOutgoingReferenceTypes,
  traversedIncomingReferenceTypes,
  referenceTypes,
  viewId,
  viewInstanceId,
  onViewSettingsUpdate,
  activeGroups,
  isViewBusy,
  isViewpointMode,
  quickPerspectiveReferenceTypeFilters,
  i18nLocale,
}: GetLeftMenuArgs): SettingsConfig[] => {
  const layoutDirectionOptions = BLOCK_DIAGRAM_LAYOUT_OPTIONS.map(
    getLayoutDirectionOption(viewSettings, viewId, i18nLocale)
  );
  const useNewGrouping = hasFeature(Features.GROUPING_RESEARCH)
    ? newGrouping(viewSettings, onViewSettingsUpdate, i18nLocale)
    : null;

  const isExpandCollapseButtonsDisabled =
    activeGroups.length === 0 || Boolean(isViewBusy);
  const collapseAllConfig = collapseAll(
    viewSettings,
    viewId,
    viewInstanceId,
    isExpandCollapseButtonsDisabled,
    i18nLocale
  );
  const expandAllConfig = expandAll(
    viewSettings,
    viewId,
    viewInstanceId,
    isExpandCollapseButtonsDisabled,
    i18nLocale
  );
  if (isViewpointMode) {
    return [
      {
        id: 'layoutDirection',
        type: SettingsType.DROPDOWN,
        iconName: IconName.SORT_ASC,
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.layoutDirection',
          'Layout direction'
        ),
        options: layoutDirectionOptions,
        dropdownSize: DropdownSize.S,
      },
      separateReferencesDropdown(
        ViewIds.BLOCK_DIAGRAM,
        viewSettings.separateReferences,
        onViewSettingsUpdate,
        i18nLocale
      ),
    ].filter(isTruthy);
  }
  return [
    dropdownTraversalMenu({
      viewSettings,
      traversedOutgoingReferenceTypes,
      traversedIncomingReferenceTypes,
      referenceTypes,
      viewId,
      maximumDepth: 99,
      onViewSettingsUpdate,
      quickPerspectiveReferenceTypeFilters,
      i18nLocale,
    }),
    {
      id: 'layout-direction',
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.selectLayoutDirection',
        'Select layout direction'
      ),
      iconName: IconName.SORT_ASC,
      type: SettingsType.DROPDOWN,
      options: layoutDirectionOptions,
    },
    showOnlyConnectedComponents(
      viewSettings.showOnlyConnectedComponents,
      onViewSettingsUpdate
    ),
    collapseAllConfig,
    expandAllConfig,
    separateReferencesToggle(viewSettings, onViewSettingsUpdate),
    useNewGrouping,
  ].filter(isTruthy);
};

const getVisualDiffOptions = (
  isInSync: boolean | undefined,
  i18nLocale: I18nLocale
) => {
  const isActive = isInScopeDiffMode() || isLoadingScope();

  return isScenarioMode()
    ? [
        {
          id: 'showVisualDiff',
          label: isActive ? 'Close visual diff' : 'Open visual diff',
          iconName: isActive ? IconName.DIFF : IconName.DIFF_EMPTY,
          isActive: isActive,
          isDisabled: !isInSync,
          onClick: () => toggleVisualDiff({ clickSource: 'settingsBar' }),
          type: SettingsType.BUTTON,
          popoverText: isInSync
            ? undefined
            : getScenarioOutOfSyncPopoverText(i18nLocale),
        },
      ]
    : [];
};

type GetRightMenuArgs = Pick<
  Parameters<typeof getRightMenuConfig>[0],
  'viewId' | 'onViewSettingsUpdate' | 'legendOnClick'
> & {
  viewSettings: BlockDiagramViewSettings;
  getContainer: () => Element | null;
  getGraphComponent: () => GraphComponent | null;
  isViewpointMode: boolean;
  isInSync?: boolean;
  isEmptyView: boolean;
  showExportFailureModal?: (format: EXPORT_FORMAT) => void;
  i18nLocale: I18nLocale;
  /** render canvas visuals to true vector SVG on SVG export */
  canvasToSvg?: boolean;
};

export const getRightMenu = ({
  viewSettings,
  getContainer,
  getGraphComponent,
  viewId,
  isViewpointMode,
  isInSync,
  legendOnClick,
  onViewSettingsUpdate,
  isEmptyView,
  showExportFailureModal,
  i18nLocale,
  canvasToSvg,
}: GetRightMenuArgs) => [
  ...getVisualDiffOptions(isInSync, i18nLocale),
  ...getRightMenuConfig({
    isViewpointMode: false,
    viewId,
    viewstate: viewSettings,
    exports: {
      ...getExportsForYFilesView({
        container: getContainer,
        graphComponent: getGraphComponent,
        exportedViewMetadata: { name: viewId, isViewpointMode },
        trackingFunction: trackExportingVisualization,
        addToPresentation,
        getStyleSheets,
        showExportFailureModal,
        exportToLucidchart: () =>
          exportToLucidchart(viewSettings.isLegendActive),
        labels: getExportLabels(i18nLocale),
        canvasToSvg,
      }),
      isDisabled: isEmptyView,
    },
    withLegend: viewHasLegend(viewId),
    knowledgeBaseLink: KnowledgeBaseLink.BLOCK_DIAGRAM,
    legendOnClick: viewHasLegend(viewId) ? legendOnClick : undefined,
    onViewSettingsUpdate,
    i18nLocale,
  }),
];
